import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Save, Loader2, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Per-org E-Invoice portal credentials dialog.
 *
 * These are the GSTIN-level username/password each business generates on
 * einvoice1.gst.gov.in (separate from app login). The GSP forwards these
 * to NIC IRP on every auth call. Stored encrypted at rest.
 */
export function EInvoiceCredentialsDialog({ open, onOpenChange, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [creds, setCreds] = useState({ gstin: '', einvoice_username: '', einvoice_password: '' });
  const [meta, setMeta] = useState({ is_password_configured: false, is_complete: false });

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    setLoading(true);
    axios.get(`${API}/einvoice/credentials`)
      .then((r) => {
        if (cancel) return;
        setCreds({
          gstin: r.data.gstin || '',
          einvoice_username: r.data.einvoice_username || '',
          einvoice_password: '',
        });
        setMeta({ is_password_configured: !!r.data.is_password_configured, is_complete: !!r.data.is_complete });
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load creds'))
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [open]);

  const save = async () => {
    if (!creds.gstin || !creds.einvoice_username || !creds.einvoice_password) {
      toast.error('Fill in GSTIN, username, and password to save.');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/einvoice/credentials`, creds);
      toast.success('E-Invoice credentials saved.');
      onSaved?.();
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-6 sm:p-7" data-testid="einvoice-creds-dialog">
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" /> E-Invoice Portal Credentials
          </DialogTitle>
          <DialogDescription className="pt-1.5">
            Paste the credentials you registered on <a href="https://einvoice1.gst.gov.in" target="_blank" rel="noopener noreferrer" className="link-theme-primary">einvoice1.gst.gov.in</a>.
            These are stored encrypted and used only when generating IRNs.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-slate-500 flex items-center gap-2 py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="einvoice-gstin">GSTIN</Label>
              <Input
                id="einvoice-gstin"
                value={creds.gstin}
                onChange={(e) => setCreds({ ...creds, gstin: e.target.value.toUpperCase() })}
                maxLength={15}
                placeholder="22AAAAA0000A1Z5"
                className="font-mono"
                data-testid="einvoice-creds-gstin"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="einvoice-username">E-Invoice Username</Label>
              <Input
                id="einvoice-username"
                value={creds.einvoice_username}
                onChange={(e) => setCreds({ ...creds, einvoice_username: e.target.value })}
                placeholder="username from einvoice1.gst.gov.in"
                data-testid="einvoice-creds-username"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="einvoice-password" className="flex items-center justify-between">
                <span>E-Invoice Password</span>
                {meta.is_password_configured && <span className="text-emerald-600 text-[10px] font-normal">already saved</span>}
              </Label>
              <div className="relative">
                <Input
                  id="einvoice-password"
                  type={showPwd ? 'text' : 'password'}
                  value={creds.einvoice_password}
                  onChange={(e) => setCreds({ ...creds, einvoice_password: e.target.value })}
                  placeholder={meta.is_password_configured ? '••••••• (re-enter to update)' : 'paste password'}
                  className="pr-10"
                  data-testid="einvoice-creds-password"
                />
                <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors" tabIndex={-1} aria-label={showPwd ? 'Hide password' : 'Show password'}>
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
              <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="einvoice-creds-cancel">
                <X className="w-4 h-4 mr-1.5" /> Close
              </Button>
              <Button onClick={save} disabled={saving} className="bg-theme-primary text-white" data-testid="einvoice-creds-save">
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                Save
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default EInvoiceCredentialsDialog;
